import { Fragment, useState, type ReactNode } from "react";
import { StyleSheet, View } from "react-native";
import { useTranslation } from "react-i18next";
import type {
  FormBadge,
  HomeWeekly,
  HomeWidgetId,
  StoredWidgetLayout,
  RecentPr,
  TodaySession,
  UnitsPreference,
  WeeklyTargetsData,
} from "@heraion/shared";
import { HOME_LAYOUT_SURFACE } from "@heraion/shared";

import type { ProgressDeps } from "../Progress/types";
import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { Button } from "../primitives/Button";
import { Eyebrow } from "../primitives/Eyebrow";
import { makeHomeBoxStyle } from "./glanceCardStyles";
import { HomeHero } from "./HomeHero";
import { FormBadgeCard } from "./FormBadgeCard";
import { RecentPrsStrip } from "./RecentPrsStrip";
import { WeeklyTargetsCard } from "./WeeklyTargetsCard";
import { HomeConfigSheet } from "./HomeConfigSheet";
import { HOME_WIDGET_COLUMN } from "./homeWidgetCatalog";
import { WidgetColumns } from "./WidgetColumns";
import { homeInsightWidgets } from "./HomeInsightWidgets";
import { useWidgetLayout, type SaveWidgetLayout } from "./useWidgetLayout";

// THE Home surface (HR-S4, formerly Plan/PlanView) — one composed tree for
// mobile's Home tab AND the web-rnw console: what you're following (carousel
// / today hero), the glanceable signals, and the widgets the athlete turns
// on (HR-11). The inbox lives in the bell only (#295). The host owns the scroll container (mobile adds
// pull-to-refresh) and mounts the data-heavy cards as slots since their
// wiring differs per platform; hero / badge / PRs / weekly are pure props.
export function HomeView({
  theme,
  today,
  resolveName,
  heroLoading,
  isStartingToday,
  onStartToday,
  onNewWorkout,
  onOpenLibrary,
  formBadge,
  weekly,
  weeklyTargets,
  onOpenProgress,
  hasAthleteTargets,
  onSetGoals,
  prs,
  units,
  locale,
  programHighlightSlot,
  quickWeightSlot,
  quickGlucoseSlot,
  progressDeps,
  measurementsSlot,
  onOpenPainMap,
  homeLayout,
  onSaveHomeLayout,
  sheetInsets,
}: {
  theme: UiTheme;
  // ——— Home hero ———
  today: TodaySession | null;
  resolveName: (slug: string, snapshot?: string) => string;
  heroLoading: boolean;
  isStartingToday: boolean;
  onStartToday: () => void;
  /** The blank hero's primary CTA (host nav / seam — web hints log-on-mobile). */
  onNewWorkout: () => void;
  /** The blank hero's pointer to the ready-made programs (#120): the host
   *  opens its Create hub, which carries the Library. */
  onOpenLibrary?: () => void;
  // ——— Glanceable signals ———
  formBadge: FormBadge | null;
  /** Calendar-week stats (shared computeHomeWeekly); null hides the card. */
  weekly: HomeWeekly | null;
  /** AP-3 three-zone meter data (shared computeWeeklyTargets); null hides it. */
  weeklyTargets: WeeklyTargetsData | null;
  /** Badge + PRs + weekly tap target → Progress (host nav / seam). */
  onOpenProgress: () => void;
  /** ME-6 — has the athlete set any weekly targets? `false` swaps the meter for
   *  the "set your goals" empty-state; undefined (loading) keeps the meter. */
  hasAthleteTargets?: boolean;
  /** ME-6 — host-injected CTA for the empty-state (Me/Obiettivi lands in ME-1);
   *  omit ⇒ the invite shows without a button, never a broken one. */
  onSetGoals?: () => void;
  prs: RecentPr[];
  units: UnitsPreference;
  locale: string;
  // ——— Host-mounted sections (platform-specific wiring) ———
  /** The followed-programs carousel (host-wired: mobile starts on the logger,
   *  web is management-only). When provided it REPLACES the HomeHero — the
   *  carousel owns the "what do I train next" slot. Omit (undefined) when
   *  there are no followed programs so the blank HomeHero shows instead. */
  programHighlightSlot?: ReactNode;
  quickWeightSlot: ReactNode;
  /** Quick glucose entry (slice A). Optional — host omits where unused. */
  quickGlucoseSlot?: ReactNode;
  // HR-11 fetta 2: widgets that start off.
  /** The self Progress deps (the host's one session-results cache). Building
   *  them costs no fetch; a panel fetches only once its widget is on. */
  progressDeps?: ProgressDeps;
  /** Host-mounted HomeMeasurementsWidget; its data hook runs only when the
   *  widget is on. */
  measurementsSlot?: ReactNode;
  onOpenPainMap?: () => void; // HR-11: opens the pain map editor (Settings -> Body); absent = read-only.
  // HR-11: the athlete's own Home.
  /** Stored order + hidden widgets (Profile.homeLayout); absent = default. */
  homeLayout: StoredWidgetLayout | undefined;
  /** Host write seam (mobile patchProfile, web useProfileDoc). */
  onSaveHomeLayout: SaveWidgetLayout;
  /** Safe-area insets for the config page sheet; web passes none. */
  sheetInsets?: { top: number; bottom: number };
}) {
  const { t } = useTranslation();
  const { wide } = useBreakpoint();
  const styles = makeStyles(theme);
  const home = useWidgetLayout(HOME_LAYOUT_SURFACE, { stored: homeLayout, save: onSaveHomeLayout });
  const [configOpen, setConfigOpen] = useState(false);
  const openConfig = () => setConfigOpen(true);

  // The "what to train next" headline: followed-programs carousel when there
  // are any (it owns day selection + run + manage), otherwise the action-first
  // blank/today hero. Full-width on both layouts. The wrapper draws the ONE
  // box (same idiom as every other Home card) around WHICHEVER variant is in
  // the slot — the variants themselves are chrome-less, so they can't drift
  // apart or double-box (HR-9).
  const hero = (
    <View style={styles.heroBox}>
      {programHighlightSlot ?? (
        <HomeHero
          today={today}
          resolveName={resolveName}
          theme={theme}
          loading={heroLoading}
          isStarting={isStartingToday}
          onStartToday={onStartToday}
          onNewWorkout={onNewWorkout}
          {...(onOpenLibrary ? { onOpenLibrary } : {})}
        />
      )}
    </View>
  );

  // The glanceable pieces, composed differently per layout below.
  const readiness = (
    <FormBadgeCard
      badge={formBadge}
      theme={theme}
      onPress={onOpenProgress}
    />
  );
  // AP-3 three-zone meter, now the ONE "this week" card: it folds the
  // calendar-week workout count + volume into its collapsed header (WeeklyStatsCard
  // retired, AP-1b-u) and scores tonnage + exercise targets, not just hard sets.
  // Sits right after readiness. Null (nothing trained + no targets) hides it.
  const targetsCard = (
    <WeeklyTargetsCard
      data={weeklyTargets}
      theme={theme}
      units={units}
      locale={locale}
      weekly={weekly}
      resolveExerciseName={resolveName}
      onOpenProgress={onOpenProgress}
      hasAthleteTargets={hasAthleteTargets}
      onSetGoals={onSetGoals}
    />
  );
  const prsStrip = (
    <RecentPrsStrip
      prs={prs}
      resolveName={resolveName}
      theme={theme}
      units={units}
      locale={locale}
      onPress={onOpenProgress}
    />
  );

  // The "Badges" group (HR-9b, Antenore 2026-07-04): the PR gallery under
  // its group header. No outer wrapper card (card-in-card nesting is banned).
  // No PRs yet: the strip draws nothing, so the header goes too.
  const badgesGroup =
    prs.length === 0 ? null : (
      <View>
        <Eyebrow theme={theme} style={styles.groupTitle}>
          {t("mobile.home.badgesTitle", { defaultValue: "Badges" })}
        </Eyebrow>
        {prsStrip}
      </View>
    );

  // HR-11: ONE order list places every widget. Wide keeps each widget in its
  // catalog column (MAIN content stream, RAIL act-now stream) and follows the
  // list inside it; narrow is the list, top to bottom. Hidden ones are gone.
  // Keyed by id so a reorder moves a card instead of remounting it.
  const widgets: Record<HomeWidgetId, ReactNode> = {
    hero,
    readiness,
    weeklyTargets: targetsCard,
    quickWeight: quickWeightSlot,
    quickGlucose: quickGlucoseSlot,
    badges: badgesGroup,
    ...homeInsightWidgets({ theme, locale, progressDeps, measurementsSlot, onOpenPainMap }),
  };
  const place = (id: HomeWidgetId) => (
    <Fragment key={id}>{widgets[id]}</Fragment>
  );
  // Wide: no gap, each card carries its own s5 margin so the columns keep the
  // hero's edge; the rail's inset lines its first card up with the hero box.
  const columns = (
    <WidgetColumns
      order={home.layout.order}
      columnOf={(id) => HOME_WIDGET_COLUMN[id]}
      wide={wide}
      renderItem={place}
      railTopInset={theme.spacing.s4}
    />
  );

  // The only door to the config sheet (PG-63: no hold on the cards): top
  // right of the wide Home, under it on narrow.
  const customize = (
    <Button
      theme={theme}
      variant="ghost"
      icon="settings"
      label={t("mobile.home.config.open", { defaultValue: "Customize Home" })}
      onPress={openConfig}
      style={wide ? null : styles.customize}
    />
  );
  const sheet = (
    <HomeConfigSheet
      theme={theme}
      visible={configOpen}
      onClose={() => setConfigOpen(false)}
      layout={home.layout}
      onMove={home.move}
      onHide={home.hide}
      onShow={home.show}
      onReset={home.reset}
      isDefault={home.isDefault}
      saveFailed={home.saveFailed}
      topInset={sheetInsets?.top}
      bottomInset={sheetInsets?.bottom}
    />
  );

  if (wide) {
    return (
      <>
        <View style={styles.header}>{customize}</View>
        {columns}
        {sheet}
      </>
    );
  }

  return (
    <>
      {columns}
      {customize}
      {sheet}
    </>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s } = theme;
  return StyleSheet.create({
    // The one hero box: same idiom as every Home card, plus surface fill and
    // an s4 top margin — the hero is the first element under the tab header
    // and was gluing to it (Antenore 2026-07-04).
    heroBox: {
      ...makeHomeBoxStyle(theme),
      backgroundColor: p.surface,
      marginTop: s.s4,
    },
    // Group header for the closing Badges section — the shared <Eyebrow>
    // voice (ink3 sm 700 +0.4) plus this group's own s5 gutter.
    groupTitle: {
      marginHorizontal: s.s5,
      marginBottom: s.s2,
    },
    // HR-11: the config door. Quiet on purpose: centred under the narrow
    // Home, at the right end of the wide Home's header row.
    customize: { alignSelf: "center", marginBottom: s.s5 },
    header: {
      flexDirection: "row",
      justifyContent: "flex-end",
      paddingHorizontal: s.s5,
      paddingTop: s.s4,
    },
  });
}
